import { disableLoader, enableLoader, showError, showSuccess } from '@context/actions';
import { updateUserData } from '@context/actions/user';
import { useSharedLinkId, useSharedLinkResource } from '@hook/useSharedLinkResource';
import SharedLinkState from '@module/sharedLinkState';
import UserRegistrationModal from '@module/userRegistration';
import { getFormTemplateByTemplateAndGuestId, getFormTemplateByTemplateId, submitFormTemplate } from '@storeData/customForms';
import { useEffect, useState } from 'react';
import { useCookies } from "react-cookie";
import { useDispatch, useSelector } from 'react-redux';
import { BaseElement, FormTemplate } from './types';

function CustomForms({ initialFormDetails = null }: { initialFormDetails: FormTemplate | null }) {
    const storeData = useSelector((state: any) => state.store ? state.store.storeData : null);
    const formTemplateId = useSharedLinkId();
    const [formDetails, setFormDetails] = useState<FormTemplate | null>(initialFormDetails)
    const [elements, setElements] = useState<BaseElement[]>([])
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [isAlreadyFilled, setIsAlreadyFilled] = useState<any>(null)
    const [cookie, setCookie] = useCookies();
    const [userData, setUserData] = useState(cookie['user']);
    const [showUserRegistration, setShowUserRegistration] = useState(false);
    const { configData } = useSelector((state: any) => state.store ? state.store.storeData : null);
    const editCustomForm = Boolean(configData?.storeConfig?.sparkConfig?.userConfig?.editCustomForm)
    const dispatch = useDispatch();
    const {
        data: formResource,
        state: formFetchState,
        retry: retryForm
    } = useSharedLinkResource<any>({
        id: formTemplateId,
        enabled: Boolean(storeData && !initialFormDetails?.id),
        refreshKey: userData?.id,
        fetcher: async (id) => {
            const existing = userData?.id
                ? await getFormTemplateByTemplateAndGuestId(id, userData.id)
                : null;
            return {
                details: existing || await getFormTemplateByTemplateId(id),
                existing
            };
        }
    });

    useEffect(() => {
        if (!initialFormDetails?.id && formFetchState === 'loading') {
            setFormDetails(null);
            setIsAlreadyFilled(null);
        }
        if (formResource?.details) {
            setFormDetails(formResource.details);
            setIsAlreadyFilled(formResource.existing);
        }
        if (Boolean(initialFormDetails?.id)) {
            setFormDetails(initialFormDetails);
            setIsAlreadyFilled(initialFormDetails)
        }
        if (!userData) {
            setShowUserRegistration(true)
        }
    }, [formFetchState, formResource, userData, initialFormDetails])

    useEffect(() => {
        if (formDetails?.elements) {
            const sorted = [...formDetails.elements].sort((a, b) => (a.elementIndex || 0) - (b.elementIndex || 0))
            setElements(sorted as BaseElement[])
        }
    }, [formDetails])

    const updateElementValue = (index: number, value: any) => {
        setElements((prev) => {
            const next = [...prev]
            next[index] = { ...next[index], value }
            return next
        })
    }

    const validateRequired = () => {
        if (!elements || elements.length === 0) return true
        for (const el of elements) {
            if (el.required) {
                if (el.elementType === 'CHECKBOX') {
                    if (!Boolean(el.value)) return false
                } else {
                    if (el.value === undefined || el.value === null || String(el.value).trim() === '') return false
                }
            }
        }
        return true
    }

    const handleSubmit = async () => {
        if (!formDetails || !storeData) return
        if (!validateRequired()) {
            dispatch(showError('Please fill all required fields'))
            return
        }
        setSubmitting(true)
        dispatch(enableLoader());
        try {
            const isUpdate = Boolean(isAlreadyFilled?.id)
            const elementsPayload = elements.map((element) => (
                isUpdate ? { ...element } : { ...element, id: null }
            ))
            const payload: any = {
                id: isUpdate ? isAlreadyFilled.id : null,
                guestId: userData.id,
                guestName: userData.firstName,
                customFormTemplateId: formDetails.customFormTemplateId ? formDetails.customFormTemplateId : formDetails.id,
                storeId: storeData.storeId,
                tenantId: storeData.tenantId,
                formName: formDetails.formName,
                description: formDetails.description,
                active: true,
                elements: elementsPayload,
            }
            const submitted: any = await submitFormTemplate(payload)
            setIsAlreadyFilled(submitted)
            setFormDetails(submitted)
            dispatch(showSuccess('Form submitted successfully'))
        } catch (e) {
            console.error(e)
            dispatch(showError('Failed to submit form'))
        } finally {
            setSubmitting(false)
            dispatch(disableLoader());
        }
    }


    return (
        <div className='custom-forms-wrapper'>
            {formDetails ? (
                <div className='card custom-forms-card'>
                    {!Boolean(userData) ? <div className='error'>Please login first to fill the form</div> : null}
                    <div className='form-title-row'>
                        <div className='form-title'>{formDetails?.formName}</div>
                        {Boolean(isAlreadyFilled) ? <span className={`status-chip success`}>Submitted</span> :
                            <span className={`status-chip pending`}>Not submitted</span>}
                    </div>
                    <div className='form-subtitle'>{formDetails?.description}</div>

                    <div className={(!Boolean(userData) || (Boolean(isAlreadyFilled) && !editCustomForm)) ? "disabled" : ""}>
                        {elements?.map((item: BaseElement, index: number) => {
                            return (
                                <div key={index} className="element-wrap">
                                    {(item.elementType !== 'CHECKBOX' && item.elementType !== 'TITLE' && item.elementType !== 'SUBTITLE') && (
                                        <div className="preview-text">{item.label}
                                            {item.required && <span className="error">*</span>}
                                        </div>
                                    )}
                                    <div className='element-input-wrap'>
                                        {item.elementType === 'TITLE' && <div className='preview-title'>{item.label}</div>}
                                        {item.elementType === 'SUBTITLE' && <div className='preview-subtitle'>{item.label}</div>}
                                        {item.elementType === 'INPUT' && (
                                            <input
                                                type="text"
                                                id={`label-${item.elementType}-${index}`}
                                                autoComplete="off"
                                                className={`input-width preview-text`}
                                                value={item.value || ''}
                                                onChange={(e) => updateElementValue(index, e.target.value)}
                                                placeholder={item.placeholder || ''}
                                            />
                                        )}
                                        {item.elementType === 'TEXTAREA' && (
                                            <textarea
                                                id={`label-${item.elementType}-${index}`}
                                                autoComplete="off"
                                                className={`input-width preview-text`}
                                                value={item.value || ''}
                                                onChange={(e) => updateElementValue(index, e.target.value)}
                                                placeholder={item.placeholder || ''}
                                            />
                                        )}
                                        {item.elementType === 'CHECKBOX' && (
                                            <label className='checkbox-row'>
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(item.value)}
                                                    onChange={(e) => updateElementValue(index, e.target.checked)}
                                                />
                                                <span className='preview-text'>{item.label}
                                                    {item.required && <span className="error">*</span>}
                                                </span>
                                            </label>
                                        )}
                                        {item.elementType === 'DATE' && (
                                            <input
                                                type="date"
                                                id={`label-${item.elementType}-${index}`}
                                                autoComplete="off"
                                                className={`input-width preview-text`}
                                                value={item.value || ''}
                                                onChange={(e) => updateElementValue(index, e.target.value)}
                                                placeholder={item.placeholder || ''}
                                                onClick={(e) => (e.target as HTMLInputElement).showPicker()}
                                                onKeyDown={(e) => e.preventDefault()}
                                            />
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    <div className={`form-actions ${Boolean(userData) ? "" : "disabled"}`}>
                        {!(Boolean(isAlreadyFilled) && !editCustomForm) && (
                            <button className="primary-btn rounded-btn" disabled={!Boolean(userData)} onClick={handleSubmit}>
                                {Boolean(isAlreadyFilled) ? 'Update' : 'Submit'}
                            </button>
                        )}
                    </div>
                </div>
            ) : <SharedLinkState
                state={initialFormDetails?.id ? 'ready' : formFetchState}
                onRetry={retryForm}
                loadingText="Loading the form..."
                notFoundText="Invalid form link."
                errorText="We couldn't load this form. Please check your connection and try again."
            />}
            {showUserRegistration && <UserRegistrationModal
                handleResponse={(user) => {
                    if (user) {
                        dispatch(updateUserData(user));
                        setUserData(user)
                        setShowUserRegistration(false);
                    }
                }}
                isApppGrpChangeOnUserGdrChange={true}
                open={showUserRegistration}
                fromPage={'FORM'}
                heading={'Login for form submission'}
                regMandatory={true}
            />}
        </div>
    )
}

export default CustomForms